import SearchBar from "@/ui-components/search-bar";
import { useDeleteHeadline } from "./api";
import { useSearchHeadlines } from "@/shared/api/newsquawk-headlines";
import NewsquawkHeadlineItem from "./NewsquawkHeadlineItem";
import { groupHeadlinesByDate } from "./api/utils";
import React, { useEffect, useMemo, useState } from "react";
import { useAccessControl } from "@/hooks/useAccessControl";
import { buildContentHubTextFilter } from "@/features/content-hub";
import { useContentHubHeadlinesSearch } from "./hooks/useContentHubHeadlinesSearch";
import { GroupedVirtuoso } from "react-virtuoso";
import toast from "react-hot-toast";

import { Headline } from "./models/Headline";
import EditHeadlineFormDialog from "./EditHeadlineFormDialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { LoadMoreButton } from "@/ui-components/load-more-button";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";

import * as Sentry from "@sentry/react";
import { trackSearchSubmit, trackSearchResults } from "@/utils/gtm";
import { DateSectionHeader } from "../firehose-widgets/shared/DateSectionHeader";
import { useWidgetSearchState } from "@/hooks/useWidgetSearchState";

export type CommonSearchProps = {
  setTitle?: (title: string) => void;
  onClose?: () => void;
  /** Whether the form UI is expanded. When false the form is hidden but the
   *  component stays mounted so results persist (set by WidgetContainer). */
  formOpen?: boolean;
  /** Per-tab/per-widget key for persisting the search (mirrors widgetFilters). */
  persistKey?: string;
  /** Reports whether a search is currently in play, so the container keeps the
   *  component mounted after Done. */
  onActiveChange?: (active: boolean) => void;
  [key: string]: unknown;
};

function HeadlinesSearchForm(
  props: CommonSearchProps & { tagIds?: string[]; useLocalStore?: boolean }
) {
  const { hasLiveAccess } = useAccessControl();

  // Legacy REST search — the fallback for demo / non-live users (content-hub
  // requires an authenticated live session).
  const {
    isLoading: legacyIsLoading,
    isSuccess: legacyIsSuccess,
    isError: legacyIsError,
    searchHeadlines,
    searchResults: legacySearchResults,
    clearSearchResults: clearLegacySearch,
    loadNext: legacyLoadNext,
    hasMore: legacyHasMore,
    isLoadingMore: legacyIsLoadingMore,
  } = useSearchHeadlines();

  const [editingHeadline, setEditingHeadline] = useState<Headline | null>(null);

  const [deletingHeadline, setDeletingHeadline] = useState<Headline | null>(
    null
  );

  const { deleteHeadlineById } = useDeleteHeadline();

  const { setTitle, formOpen = true, persistKey, onActiveChange } = props;
  const [savedSearch, setSavedSearch] = useWidgetSearchState(persistKey);

  const [exactMatch, setExactMatch] = React.useState(
    savedSearch.exactMatch ?? false
  );
  const [query, setQuery] = React.useState<string>(savedSearch.query ?? "");

  const [startDate, setStartDate] = React.useState<string>(
    savedSearch.startDate ?? ""
  );
  const [startTime, setStartTime] = React.useState<string>(
    savedSearch.startTime ?? "00:00"
  );
  const [endDate, setEndDate] = React.useState<string>(
    savedSearch.endDate ?? ""
  );
  const [endTime, setEndTime] = React.useState<string>(
    savedSearch.endTime ?? "23:59"
  );

  // Committed search inputs — a submit (Enter / Search / persisted re-run)
  // commits these; they drive the content-hub query. Typing alone doesn't fetch.
  const [activeQuery, setActiveQuery] = React.useState<string>(
    savedSearch.query ?? ""
  );
  const [activeExactMatch, setActiveExactMatch] = React.useState<boolean>(
    savedSearch.exactMatch ?? false
  );
  // Committed UTC ISO date bounds (content-hub `occurs_at` range).
  const [activeDateFrom, setActiveDateFrom] = React.useState<string | undefined>(
    undefined
  );
  const [activeDateTo, setActiveDateTo] = React.useState<string | undefined>(
    undefined
  );

  // Content-hub search (live users) — the same pipeline as the feed + funnel
  // panel. Built from the header query + date range via the shared filter logic.
  const contentHubFilter = useMemo(
    () =>
      buildContentHubTextFilter({
        query: activeQuery,
        exactMatch: activeExactMatch,
        dateFrom: activeDateFrom,
        dateTo: activeDateTo,
      }),
    [activeQuery, activeExactMatch, activeDateFrom, activeDateTo]
  );
  // A committed text query OR a date bound is enough to run a content-hub search.
  const hasCriteria =
    !!activeQuery.trim() || !!activeDateFrom || !!activeDateTo;
  const {
    headlines: chHeadlines,
    isLoading: chIsLoading,
    isError: chIsError,
    hasMore: chHasMore,
    loadNextPage: chLoadNext,
    loadingNextPage: chIsLoadingMore,
  } = useContentHubHeadlinesSearch(contentHubFilter, {
    enabled: hasLiveAccess && hasCriteria,
  });

  // Live users search content-hub; demo / non-live fall back to legacy REST.
  const searchResults = hasLiveAccess ? chHeadlines : legacySearchResults;
  const isLoading = hasLiveAccess ? chIsLoading : legacyIsLoading;
  const isError = hasLiveAccess ? chIsError : legacyIsError;
  const hasMore = hasLiveAccess ? chHasMore : legacyHasMore;
  const isLoadingMore = hasLiveAccess ? chIsLoadingMore : legacyIsLoadingMore;
  const loadNext = hasLiveAccess ? chLoadNext : legacyLoadNext;
  // content-hub exposes no `isSuccess`; committed criteria that have finished
  // loading (with no error) are our success signal, so "no results" can render.
  const isSuccess = hasLiveAccess
    ? hasCriteria && !chIsLoading && !chIsError
    : legacyIsSuccess;

  const clearSearchResults = React.useCallback(() => {
    setActiveQuery("");
    setActiveExactMatch(false);
    setActiveDateFrom(undefined);
    setActiveDateTo(undefined);
    clearLegacySearch();
  }, [clearLegacySearch]);

  // Convert a local date ("YYYY-MM-DD") + time ("HH:MM") into a UTC ISO string.
  // Start floors to the minute; end fills to :59.999 so the whole end minute is
  // inclusive. Empty date → undefined (open-ended side).
  const toISO = (date: string, time: string, isEnd: boolean): string | undefined => {
    if (!date) return undefined;
    const [h, m] = (time || (isEnd ? "23:59" : "00:00")).split(":").map(Number);
    const d = new Date(date);
    d.setHours(h, m, isEnd ? 59 : 0, isEnd ? 999 : 0);
    return d.toISOString();
  };

  const { groups, groupedCounts } = useMemo(
    () => groupHeadlinesByDate(searchResults),
    [searchResults]
  );

  const showSearchResults = searchResults.length > 0 || isSuccess;

  // A committed search still loading its first page — drives a spinner in the
  // results overlay so the box gives feedback while it fetches (previously the
  // overlay only appeared once the search had settled).
  const isSearchPending =
    hasCriteria && isLoading && searchResults.length === 0;

  const formRef = React.useRef<HTMLDivElement>(null);

  const [formHeight, setFormHeight] = React.useState<number>(70);

  // A date range only narrows the content-hub (live) path; the legacy REST
  // fallback is keyword-only, so a date bound alone can only search for live users.
  const canSearch = (q: string): boolean =>
    !!q.trim() || (hasLiveAccess && (!!startDate || !!endDate));

  // Commits the current inputs. No store write — used by the Search button/Enter
  // (via handleSearch) and by the on-mount re-run of a saved search.
  const runSearch = (q: string) => {
    if (!canSearch(q)) return;

    setActiveQuery(q);
    setActiveExactMatch(exactMatch);
    setActiveDateFrom(toISO(startDate, startTime, false));
    setActiveDateTo(toISO(endDate, endTime, true));

    // Demo / non-live users: content-hub isn't available, so use legacy REST
    // (keyword-only — the date range doesn't apply to this path).
    if (!hasLiveAccess) {
      searchHeadlines({
        query: q,
        subscribed: true,
        exactMatch,
        rows: 30,
      });
    }
  };

  const handleSearch = (q: string) => {
    // Empty Enter / Search press with nothing to search is a no-op.
    if (!canSearch(q)) return;

    runSearch(q);

    // Persist params so the search stays in play after Done and across reloads.
    setSavedSearch({ query: q, exactMatch, startDate, startTime, endDate, endTime });

    const activeFilters: string[] = [];
    if (exactMatch) activeFilters.push("exact_match");
    if (startDate || endDate) activeFilters.push("date_range");
    if (props.tagIds?.length) activeFilters.push("tag_filter");
    trackSearchSubmit({
      search_term: q,
      search_context: "headlines",
      search_filters:
        activeFilters.length > 0 ? activeFilters.join(", ") : undefined,
    });
  };

  const handleClear = () => {
    setQuery("");
    setExactMatch(false);
    setStartDate("");
    setEndDate("");
    clearSearchResults();
    setSavedSearch({});
  };

  let footer = <Empty />;

  if (isSuccess && searchResults.length === 0) {
    footer = <Footer>No results found</Footer>;
  } else if (hasMore) {
    footer = <LoadMoreButton loading={isLoadingMore} loadNextPage={loadNext} />;
  } else if (searchResults.length > 0) {
    footer = <Footer>End of search results</Footer>;
  }

  useEffect(() => {
    if (!isError) return;

    toast.error("There was an error fetching the headlines, please try again.");
    Sentry.captureMessage("Error fetching headlines in search");
  }, [isError]);

  useEffect(() => {
    if (isSuccess && hasCriteria) {
      const activeFilters: string[] = [];
      if (activeExactMatch) activeFilters.push("exact_match");
      if (activeDateFrom || activeDateTo) activeFilters.push("date_range");
      trackSearchResults({
        search_term: activeQuery,
        search_context: "headlines",
        results_count: searchResults.length,
        search_filters:
          activeFilters.length > 0 ? activeFilters.join(", ") : undefined,
      });
    }
  }, [
    isSuccess,
    hasCriteria,
    activeQuery,
    searchResults.length,
    activeExactMatch,
    activeDateFrom,
    activeDateTo,
  ]);

  useEffect(() => {
    const titleString = activeQuery
      ? `Showing results for '${activeQuery}'`
      : "Showing search results";

    if (showSearchResults) {
      setTitle?.(titleString);
    } else {
      setTitle?.("Newsquawk Headlines");
    }

    return () => setTitle?.("Newsquawk Headlines");
  }, [showSearchResults, activeQuery, setTitle]);

  // Re-apply a persisted search on mount (after Done / tab switch / reload).
  useEffect(() => {
    if (
      savedSearch.query?.trim() ||
      savedSearch.startDate ||
      savedSearch.endDate
    ) {
      runSearch(savedSearch.query ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tell the container whether a search is in play, so it keeps this component
  // mounted after Done (results persist) and drops it once cleared.
  useEffect(() => {
    onActiveChange?.(showSearchResults);
  }, [showSearchResults, onActiveChange]);

  // Clear search results when the component unmounts
  useEffect(() => clearSearchResults, [clearSearchResults]);

  // Measure and update form height dynamically
  useEffect(() => {
    const updateFormHeight = () => {
      if (formRef.current) {
        const height = formRef.current.offsetHeight;
        setFormHeight(height);
      }
    };

    // Initial measurement
    updateFormHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(updateFormHeight);
    if (formRef.current) {
      resizeObserver.observe(formRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <React.Fragment>
      <div
        className={`bg-gray-800 border-b border-gray-700/50 px-3 py-2 space-y-2${
          formOpen ? "" : " hidden"
        }`}
        ref={formRef}
      >
        {/* Search bar — auto-submits as you type; the action row below adds explicit Search/Clear/Done. */}
        <SearchBar
          value={query}
          onChange={setQuery}
          loading={isLoading}
          onSearch={handleSearch}
          onClear={handleClear}
        />

        {/* Options row: exact match + date/time range + actions. Sticky so they stay reachable on a short widget. */}
        <div className="sticky bottom-0 z-10 bg-gray-800 flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={exactMatch}
              onChange={(e) => setExactMatch(e.target.checked)}
              className="size-3.5 rounded border-gray-600 bg-gray-900 text-squawk-blue focus:ring-0 focus:ring-offset-0"
            />
            <span className="text-xs text-gray-400">Exact match</span>
          </label>

          {/* Date/time range — content-hub `occurs_at` filter, live users only. */}
          {hasLiveAccess && (
            <React.Fragment>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">From:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
                />
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">To:</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
                />
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
                />
              </div>
            </React.Fragment>
          )}

          <div className="ml-auto flex items-center gap-2">
            {(query || startDate || endDate || showSearchResults) && (
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-1 text-xs font-medium rounded text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => handleSearch(query)}
              disabled={!canSearch(query)}
              className="px-3 py-1 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Search
            </button>
            {props.onClose && (
              <button
                onClick={props.onClose}
                className="px-3 py-1 text-xs font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>

      {(showSearchResults || isSearchPending) && (
        <section
          className="absolute z-30 bg-white dark:bg-gray-900 inset-0 pb-[40px] overflow-y-auto scrollbar-thin"
          style={{ top: formOpen ? `${formHeight}px` : 0 }}
        >
          {isSearchPending ? (
            // Pending first page — show a spinner (delayed) instead of an empty
            // overlay so the user gets feedback right after hitting Search.
            <EmptyPlaceholder message="" isLoading />
          ) : (
            <GroupedVirtuoso
              className="scrollbar-thin inset-x-0 top-0"
              groupCounts={groupedCounts}
              groupContent={(index) =>
                groups[index] == "Today" ? (
                  <p className="h-[1px]" />
                ) : (
                  <DateSectionHeader title={groups[index]} />
                )
              }
              itemContent={(idx) => (
                <NewsquawkHeadlineItem
                  key={searchResults[idx].id}
                  headline={searchResults[idx]}
                  onItemDelete={setDeletingHeadline}
                  onItemEdit={setEditingHeadline}
                  searchQuery={activeQuery}
                />
              )}
              components={{
                Footer: () => footer,
              }}
            />
          )}
        </section>
      )}

      {/* Dialogs */}
      <section>
        <EditHeadlineFormDialog
          isOpen={!!editingHeadline}
          headline={editingHeadline!}
          onClose={() => setEditingHeadline(null)}
        />

        <DeleteConfirmationDialog
          isOpen={!!deletingHeadline}
          onClose={() => setDeletingHeadline(null)}
          onConfirmDelete={() =>
            deletingHeadline
              ? deleteHeadlineById(deletingHeadline.id)
              : Promise.reject()
          }
        />
      </section>
    </React.Fragment>
  );
}

const Empty = () => null;

const Footer = ({ children }: { children: React.ReactNode }) => (
  <p className="font-medium text-sm text-center text-squawk-blue-light py-1">
    {children}
  </p>
);

export default HeadlinesSearchForm;
